import { useMemo } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { deriveSearchableAppPages } from '@/config/appNavigation'
import { useAppNavigation } from '@features/navigation/hooks/useAppNavigation'
import { searchMetadataPage } from '@features/search/api/search.api'
import { useDebouncedValue } from '@features/shared/hooks/useDebouncedValue'
import { sidebarQueryKeys } from '../api/queryKeys'
import {
  alphabeticalSidebarSearch,
  pageSearchResult,
  rankSidebarSearch,
  tableSearchResult,
  viewSearchResult,
  type SidebarSearchResult,
} from '../logic/sidebarSearch'
import { useRecentResults } from './useRecentPages'
import { useTableOrganization } from './useTableOrganization'

/** Views asked of the search service per search; the best come first. */
const SIDEBAR_SEARCH_LIMIT = 20
/** How long typing rests before the search service is asked. */
const SIDEBAR_SEARCH_DEBOUNCE_MS = 150

/**
 * The sidebar's search. Before anything is typed, the places the user opened
 * lately, newest first (`recent`), then every other page and table A to Z by
 * its trail (`alphabetical`), to browse. As they type, at once: every page they can
 * open, every table (the sidebar's own list of them, so `Tables` finds them
 * all by their trail) and every place opened lately, then the views the
 * search service finds, as one list ranked best first (rankSidebarSearch),
 * ties going to the one opened most lately. While the views are on their way
 * the last ones stay, kept only if they still match.
 */
export function useSidebarSearch(query: string): {
  results: SidebarSearchResult[]
  recent: SidebarSearchResult[]
  alphabetical: SidebarSearchResult[]
  isSearching: boolean
} {
  const { modules } = useAppNavigation()
  const term = query.trim()
  const settled = useDebouncedValue(term, SIDEBAR_SEARCH_DEBOUNCE_MS)
  const tablesModule = modules.find((module) => module.id === 'tables')
  const searchesViews = tablesModule !== undefined && settled.length > 0
  const { data: organization } = useTableOrganization()
  const { results: recent, recency } = useRecentResults()

  const views = useQuery({
    queryKey: sidebarQueryKeys.search('views', settled),
    // A search typing has moved on from is cancelled, not left to finish.
    queryFn: ({ signal }) =>
      searchMetadataPage('views', settled, 0, SIDEBAR_SEARCH_LIMIT, {}, signal),
    enabled: searchesViews,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  })

  const pages = useMemo(() => deriveSearchableAppPages(modules).map(pageSearchResult), [modules])
  const tableList = organization?.tables
  const viewHits = views.data?.items
  const tablesLabel = tablesModule?.label ?? 'Tables'

  // Every page and table the user can open, as results.
  const places = useMemo(() => {
    const all: SidebarSearchResult[] = [...pages]
    if (tablesModule) {
      for (const table of tableList ?? []) {
        all.push(tableSearchResult({ id: table.id, name: table.display_name_plural }, tablesLabel))
      }
    }
    return all
  }, [pages, tableList, tablesLabel, tablesModule])

  const alphabetical = useMemo(
    () => alphabeticalSidebarSearch(places, new Set(recent.map((result) => result.path))),
    [places, recent]
  )

  const results = useMemo(() => {
    if (!term) return []
    const found: SidebarSearchResult[] = [...places]
    if (tablesModule) {
      for (const hit of viewHits ?? []) {
        const result = viewSearchResult(hit, tablesLabel)
        if (result) found.push(result)
      }
    }
    // Last, so a page or table already found keeps its own kind.
    found.push(...recent)
    return rankSidebarSearch(found, term, recency)
  }, [places, recency, recent, tablesLabel, tablesModule, term, viewHits])

  const isSearching = searchesViews && (term !== settled || views.isFetching)

  return { results, recent, alphabetical, isSearching }
}
